import type { ArrayCallback, ArrayPredicate } from 'Core/types';
import FacetValue from 'Models/facetValue';
import { equalBy, uniqBy } from 'Utils/array';
import { isTruthy } from 'Utils/guards';

import type { FacetValueBase } from './facetValue';

export type VehicleData = Array<VehicleValue | { vehicleExtra: VehicleExtra }>;

export type VehicleValue = Pick<FacetValueBase, 'field' | 'term' | 'value' | 'payload' | 'imageUrl'>;
export type VehicleFieldMap = Record<string, string>;
export type NormalizedVehicleValue = Required<VehicleValue>;

export type VehicleExtra = VehicleFieldMap;

function stripVehicleValue(value: NormalizedVehicleValue): NormalizedVehicleValue {
  return {
    field: value.field,
    term: value.term,
    value: value.value,
    payload: value.payload,
    imageUrl: value.imageUrl,
  };
}

export default class Vehicle {
  static null = new Vehicle([], [], {});

  static fromSelection(
    selection: VehicleValue[],
    responseFields: string[],
    requiredFields: string[] | null,
    ignoreOptionalFields: boolean,
  ): Vehicle {
    return new Vehicle(
      responseFields
        .map(
          (field) =>
            selection.find((value) => value.field === field) ||
            (!requiredFields?.includes(field) &&
              ignoreOptionalFields && { field, term: '__ignored', value: '__ignored' }),
        )
        .filter(isTruthy),
      responseFields,
    );
  }

  // the argument may be undefined if one of vehicle values were constructed without value
  static isUsualTerm(term?: string): boolean {
    return !!term && !term.startsWith('__');
  }

  _fields: string[];
  _values: NormalizedVehicleValue[];
  _vehicleExtra: VehicleExtra;
  _logo: string = '';
  key: string;

  constructor(
    values: VehicleValue[] | VehicleFieldMap,
    fitmentFields: string[],
    vehicleExtra: VehicleExtra = {},
  ) {
    let vehicleValues: NormalizedVehicleValue[];

    if (!Array.isArray(values)) {
      vehicleValues = Object.entries(values).map(([field, value]) => {
        return normalizeVehicleValue({
          field,
          value,
          term: FacetValue.escapeTermValue(value),
        });
      });
    } else {
      vehicleValues = values.map(normalizeVehicleValue);
      this._logo = vehicleValues.find((v) => v.field === 'Make')?.imageUrl || '';
    }

    this._fields = fitmentFields;
    this._values = this._fields
      .flatMap((field) => vehicleValues.filter((v) => v.field === field))
      // vehicle values may have more properties than specified in the type, but we don't want to serialize them
      .map(stripVehicleValue);
    this.key = this._values.map(makeKey).join('&');
    this._vehicleExtra = vehicleExtra;
  }

  /* array-like methods */

  filter(predicate: ArrayPredicate<NormalizedVehicleValue>): NormalizedVehicleValue[] {
    return this._values.filter(predicate);
  }

  map<R>(callback: ArrayCallback<NormalizedVehicleValue, R>): R[] {
    return this._values.map(callback);
  }

  mapToVehicle(callback: ArrayCallback<NormalizedVehicleValue, VehicleValue>): Vehicle {
    return new Vehicle(this._values.map(callback), this._fields, { ...this._vehicleExtra });
  }

  concat(values: VehicleValue[] | VehicleValue): Vehicle {
    const normalizedValues = Array.isArray(values)
      ? values.map(normalizeVehicleValue)
      : normalizeVehicleValue(values);

    return new Vehicle(this._values.concat(normalizedValues), this._fields, { ...this._vehicleExtra });
  }

  some(predicate: ArrayPredicate<NormalizedVehicleValue>): boolean {
    return this._values.some(predicate);
  }

  /* vehicle methods */

  get notNull(): boolean {
    return !!this._values.length;
  }

  get selection(): NormalizedVehicleValue[] {
    return this.filter((v) => Vehicle.isUsualTerm(v.term));
  }

  get serializable(): VehicleData {
    return this._vehicleExtra && Object.keys(this._vehicleExtra).length
      ? [...this._values, { vehicleExtra: this._vehicleExtra }]
      : this._values;
  }

  get fieldMap(): VehicleFieldMap {
    return Object.fromEntries(this._values.map((v) => [v.field, v.value]));
  }

  get filteredFieldMap(): VehicleFieldMap {
    return Object.fromEntries(this.selection.map((v) => [v.field, v.value]));
  }

  get vehicleExtra(): VehicleExtra {
    return this._vehicleExtra;
  }

  set vehicleExtra(data: VehicleExtra) {
    this._vehicleExtra = { ...this._vehicleExtra, ...data };
  }

  get logo(): string {
    return this._logo;
  }

  getVehicleExtraValue(key: string): string | undefined {
    return this._vehicleExtra[key];
  }

  getFilteredValues(fields: string[]): string[] {
    return this.filter((v) => fields.includes(v.field) && Vehicle.isUsualTerm(v.term)).map((v) => v.value);
  }

  equals = (vehicle: Vehicle): boolean => equalBy(this._values, vehicle._values, makeKey);

  equalsBySelection = (vehicle: Vehicle): boolean => equalBy(this.selection, vehicle.selection, makeKey);

  isMoreSpecificThan = (vehicle: Vehicle): boolean =>
    // we check for repeated values because don't want to compare vehicles if one of them has several years/makes/etc.
    !hasRepeatedValues(this) &&
    !hasRepeatedValues(vehicle) &&
    !this.equals(vehicle) &&
    vehicle._fields.every((field) => {
      const { term } = this._values.find((v) => v.field === field) || {};
      const { term: another } = vehicle._values.find((v) => v.field === field) || {};
      return term === another || specificity(term) > specificity(another);
    });

  merge(vehicle: Vehicle): Vehicle | null {
    if (this._fields !== vehicle._fields) {
      return null;
    }

    if (this.equals(vehicle)) {
      return this;
    }
    // we check for repeated values because don't want to compare vehicles if one of them has several years/makes/etc.
    if (hasRepeatedValues(this) || hasRepeatedValues(vehicle)) {
      return null;
    }
    const thisMap = Object.fromEntries(this.map(({ field, term }) => [field, term]));
    const anotherMap = Object.fromEntries(vehicle.map(({ field, term }) => [field, term]));
    const values = vehicle._fields
      .reduce<(VehicleValue | undefined)[] | null>((result, field) => {
        const thisTerm = thisMap[field];
        const anotherTerm = anotherMap[field];
        if (!result) {
          return result;
        }
        if (thisTerm === anotherTerm || specificity(thisTerm) > specificity(anotherTerm)) {
          return [...result, this._values.find((v) => v.field === field)];
        }
        if (specificity(anotherTerm) > specificity(thisTerm)) {
          return [...result, vehicle._values.find((v) => v.field === field)];
        }
        return null;
      }, [])
      ?.filter((v): v is VehicleValue => v !== undefined);

    const mergedVehicleExtra = { ...this._vehicleExtra, ...vehicle._vehicleExtra };

    return values ? new Vehicle(values, vehicle._fields, mergedVehicleExtra) : null;
  }

  toString(): string {
    const fieldMap = new Map(getFieldMapEntries(this));
    return this._fields
      .flatMap((field) => {
        const values = fieldMap.get(field);
        return values && values.every(Vehicle.isUsualTerm)
          ? [values.filter(Vehicle.isUsualTerm).join('/')]
          : [];
      })
      .join(' ');
  }
}

function normalizeVehicleValue(value: VehicleValue): NormalizedVehicleValue {
  return {
    field: value.field,
    term: value.term,
    value: value.value ?? value.term,
    payload: value.payload ?? null,
    imageUrl: value.imageUrl ?? null,
  };
}

function makeKey(value: FacetValueBase) {
  return `${value.field}|${value.term}`;
}

function hasRepeatedValues({ _values }: Vehicle) {
  return _values.length !== uniqBy(_values, 'field').length;
}

function specificity(value: string | undefined) {
  // {non-special value} > __ignored > __inexact > {no value}
  return !value ? 0 : value === '__inexact' ? 1 : value === '__ignored' ? 2 : 3;
}

function getFieldMapEntries(self: Vehicle): [string, string[] | null][] {
  return self._fields.map((field) => {
    const values = self._values.filter((v) => v.field === field);
    return [field, values.length ? values.map((v) => v.value) : null];
  });
}

export function hasRequiredVehicleSelection(
  selection: VehicleValue[],
  requiredFields: string[] | null,
): boolean {
  return !requiredFields?.some((field) => !selection.some((value) => value.field === field));
}

export function updateVehicleSelection(
  selection: VehicleValue[],
  selectedValue: VehicleValue,
  fitmentFields: string[],
): VehicleValue[] {
  const updatedSelection = [...selection];
  const valueIndexToReplace = updatedSelection.findIndex((value) => value.field === selectedValue.field);
  if (valueIndexToReplace !== -1) {
    updatedSelection[valueIndexToReplace] = selectedValue;
  } else {
    updatedSelection.splice(fitmentFields.indexOf(selectedValue.field), 0, selectedValue);
  }
  return updatedSelection;
}

export function isVehicleChangedOnResponse(
  selectedVehicle: Vehicle | undefined,
  responseVehicle: Vehicle,
  allRequiredFieldsSelected: boolean,
): boolean {
  return (
    !selectedVehicle?.equals(responseVehicle) &&
    (!selectedVehicle?.isMoreSpecificThan(responseVehicle) || !allRequiredFieldsSelected)
  );
}
